import { FC, Fragment } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { cx } from "utils/classnames";
import { ContextMenuPopover } from "./ContextMenuPopover";
import {
  IconButton,
  IconButtonSize,
  IconButtonVariant
} from "shared/components";
import { DotsHorizontal } from "untitledui-js-base";

export type Crumb = {
  title: string;
  href?: string;
  isActive?: boolean;
  onClick?: () => void;
};

type Props = {
  crumbs: Crumb[];
  className?: string;
};

const Item: FC<Crumb> = ({ title, href, onClick, isActive }) => {
  const { pathname } = useLocation();

  return href ? (
    <Link
      key={href}
      to={href}
      className={cx(
        "text-sm font-medium hover:text-primary-600",
        pathname === href ? "text-primary-500" : "text-content-400"
      )}
    >
      {title}
    </Link>
  ) : (
    <button
      onClick={onClick}
      className={cx(
        "text-sm font-medium hover:text-primary-600",
        isActive ? "text-primary-500" : "text-content-400"
      )}
    >
      {title}
    </button>
  );
};

const Separator = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    className="shrink-0 stroke-content-500"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M6 12L10 8L6 4"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const BreadCrumbs: FC<Props> = ({ crumbs, className }) => {
  const navigate = useNavigate();
  return (
    <>
      <div className={cx("hidden items-center gap-x-2 md:flex", className)}>
        {crumbs.map((c, idx) => (
          <Fragment key={c.title}>
            <Item {...c} />
            {idx + 1 === crumbs.length ? null : <Separator />}
          </Fragment>
        ))}
      </div>

      <div className="flex md:hidden items-center">
        <ContextMenuPopover
          actions={crumbs.map((c) => ({
            title: c.title,
            onClick: c.onClick || (() => c.href && navigate(c.href))
          }))}
          trigger={
            <IconButton
              size={IconButtonSize.SM}
              variant={IconButtonVariant.BASE}
              borderLess
              className="translate-y-[3px] !text-content-500 min-h-[20px] max-h-[20px]"
              Icon={DotsHorizontal}
            />
          }
        />
        <div className="mr-2">
          <Separator />
        </div>
        <Item {...crumbs.at(-1)!} />
      </div>
    </>
  );
};
